import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { CalendarOff, Plus, Edit, Trash2, Save, ArrowLeft, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import Sidebar from '../../GlobalManagement/Pages/Sidebar';
import SearchBar from '../../GlobalManagement/Pages/SearchBar';
import { 
  masterBodyArea, 
  masterContentArea, 
  masterPageShell, 
  masterHeader, 
  masterHeaderCard, 
  masterHeaderInfo, 
  masterHeaderIcon, 
  masterTitle, 
  masterAccentButton, 
  masterPrimaryButton, 
  masterTableBody, 
  masterPanel, 
  masterTable, 
  masterStickyHeadRow, 
  masterTableData, 
  masterTbody, 
  masterTableRow, 
  masterTableCell, 
  masterEditButton, 
  masterDeleteButton, 
  masterFormContainer, 
  masterFormSectionHeader, 
  masterFormGrid2, 
  masterFormGridRow, 
  masterInputLabel, 
  masterInput, 
  masterSelect, 
  masterFormActionBlock, 
  masterActiveColor, 
  masterInactiveColor, 
  masterActionButtonsContainer,
  masterReadOnly,
  masterTableCellstatus,
  masterTableDatastatus
} from '../../GlobalManagement/Pages/masterPageStyles';

const LeaveTypeMaster = () => {
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [showForm, setShowForm] = useState(false); // Toggle between List view and Form view
  const [isSaving, setIsSaving] = useState(false);
  const [editId, setEditId] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(18);

  const [formData, setFormData] = useState({
    leaveTypeID: "",
    leaveName: "",
    leaveCode: "",
    totalDays: "",
    annualQuota: "",
    carryForward: "No",
    status: "Active",
    remarks: ""
  });

  const fetchLeaveTypes = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get("/api/leavetypes", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaveTypes(res.data || []);
    } catch (err) {
      toast.error("Failed to load leave types");
    }
  };

  const fetchNextLeaveTypeID = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get("/api/leavetypes/next-id", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFormData(prev => ({ ...prev, leaveTypeID: res.data.leaveTypeID || "" }));
    } catch (err) {
      setFormData(prev => ({ ...prev, leaveTypeID: "" }));
    }
  };

  useEffect(() => {
    fetchLeaveTypes();
  }, []);

  const filteredData = useMemo(() => {
    const sortedList = [...leaveTypes].sort((a, b) => {
      const idA = String(a.leaveTypeID || a._id || '');
      const idB = String(b.leaveTypeID || b._id || '');
      return idB.localeCompare(idA, undefined, { numeric: true, sensitivity: 'base' });
    });

    const q = searchTerm.trim().toLowerCase();
    if (!q) return sortedList;

    return sortedList.filter((item) => {
      const name = (item.leaveName || '').toLowerCase();
      const code = (item.leaveCode || '').toLowerCase();
      const typeId = (item.leaveTypeID || '').toLowerCase();
      return name.startsWith(q) || code.startsWith(q) || typeId.startsWith(q);
    });
  }, [leaveTypes, searchTerm]);

  const paginatedData = useMemo(() => {
    if (pageSize === -1) return filteredData;
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, pageSize]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddNew = async () => {
    setEditId(null);
    setFormData({
      leaveTypeID: "",
      leaveName: "",
      leaveCode: "",
      totalDays: "",
      annualQuota: "",
      carryForward: "No",
      status: "Active",
      remarks: ""
    });
    setShowForm(true);
    await fetchNextLeaveTypeID();
  };

  const handleEdit = (lt) => {
    setEditId(lt._id);
    setFormData({
      leaveTypeID: lt.leaveTypeID || "",
      leaveName: lt.leaveName || "",
      leaveCode: lt.leaveCode || "",
      totalDays: lt.totalDays || "",
      annualQuota: lt.annualQuota || "",
      carryForward: lt.carryForward || "No",
      status: lt.status || "Active",
      remarks: lt.remarks || ""
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this leave type?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/api/leavetypes/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Leave type deleted successfully");
      fetchLeaveTypes();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const handleSaveOrUpdate = async (e) => {
    e.preventDefault();
    if (!formData.leaveName.trim() || !formData.leaveCode.trim()) {
      toast.error("Leave name and alias are required");
      return;
    }

    const duplicate = leaveTypes.find(
      (lt) =>
        lt.leaveName.toLowerCase().trim() === formData.leaveName.toLowerCase().trim() &&
        lt._id !== editId
    );
    if (duplicate) {
      toast.error("Leave type already exists!");
      return;
    }

    setIsSaving(true);
    const token = localStorage.getItem("token");
    const config = { headers: { Authorization: `Bearer ${token}` } };

    try {
      const payload = {
        leaveTypeID: formData.leaveTypeID,
        leaveName: formData.leaveName.trim().toUpperCase(),
        leaveCode: formData.leaveCode.trim().toUpperCase(),
        totalDays: formData.totalDays,
        annualQuota: formData.annualQuota,
        carryForward: formData.carryForward,
        status: formData.status,
        remarks: formData.remarks,
      };

      if (editId) {
        await axios.put(`/api/leavetypes/${editId}`, payload, config);
        toast.success("Leave type updated successfully");
      } else {
        await axios.post("/api/leavetypes", payload, config);
        toast.success("Leave type saved successfully");
      }

      fetchLeaveTypes();
      setShowForm(false);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to save/update leave type");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={masterBodyArea}>
      <Sidebar />
       <div className={`${masterPageShell} ${masterContentArea}`}>
        {/* Header Section */}
        <header className={masterHeader}>
          <div className={masterHeaderCard}>
            <div className={masterHeaderInfo}>
              <div className={masterHeaderIcon}><CalendarOff size={14} /></div>
              <div><h1 className={masterTitle}>Leave Type Master</h1></div>
            </div>
            <button 
              type="button"
              onClick={showForm ? () => setShowForm(false) : handleAddNew} 
              className={showForm ? masterPrimaryButton : masterAccentButton}
            >
              {showForm ? <><ArrowLeft size={10} /> VIEW LIST</> : <><Plus size={10} /> ADD LEAVE TYPE</>}
            </button>
          </div>
        </header>

        <main className={`${masterTableBody} mt-1 w-full`}>
          {!showForm ? (
            <div className={`${masterPanel} flex flex-col overflow-hidden w-full`}>
              {/* Search & Pagination Toolbar */}
              <div className="border-b border-slate-200 flex-shrink-0 bg-white p-1 sm:p-2">
                <SearchBar 
                  value={searchTerm} 
                  onChange={(val) => {
                    setSearchTerm(val);
                    setCurrentPage(1);
                  }} 
                  placeholder="Search by leave name, alias or ID..." 
                  totalItems={filteredData.length} 
                  currentPage={currentPage} 
                  pageSize={pageSize} 
                  onPageChange={setCurrentPage} 
                  onPageSizeChange={setPageSize} 
                />
              </div>

              {/* Horizontally Scrollable Table Container */}
              <div className="overflow-x-auto w-full">
                <table className={masterTable}>
                  <thead>
                    <tr className={masterStickyHeadRow}>
                      <th className={masterTableData}>S.No</th>
                      
                      <th className={masterTableData}>Leave Name</th>
                      <th className={masterTableData}>Alias</th>
                      <th className={masterTableData}>Total Days</th>
                      <th className={masterTableData}>Carry Forward</th>
                      <th className={masterTableDatastatus}>Status</th>
                      <th className={masterTableDatastatus}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className={masterTbody}>
                    {paginatedData.length > 0 ? (
                      paginatedData.map((lt, index) => (
                        <tr key={lt._id} className={masterTableRow}>
                          <td className={masterTableCell}>
                            {pageSize === -1 ? index + 1 : (currentPage - 1) * pageSize + index + 1}
                          </td>
                       
                          <td className={masterTableCell} style={{ textAlign: 'left', fontWeight: '600' }}>
                            {lt.leaveName}
                          </td>
                          <td className={masterTableCell}>
                            {lt.leaveCode}
                          </td>
                          <td className={masterTableCell}>
                            {lt.totalDays || '-'}
                          </td>
                          <td className={masterTableCell}>
                            {lt.carryForward || 'No'}
                          </td>
                          <td className={masterTableCellstatus}>
                            <span className={lt.status === 'Active' ? masterActiveColor : masterInactiveColor}>
                              {lt.status || 'Active'}
                            </span>
                          </td>
                          <td className={masterTableCellstatus}>
                            <div className={masterActionButtonsContainer}>
                              <button 
                                type="button" 
                                onClick={() => handleEdit(lt)} 
                                className={masterEditButton}
                                title="Edit"
                              >
                                <Edit size={10} />
                              </button>
                              <button 
                                type="button" 
                                onClick={() => handleDelete(lt._id)} 
                                className={masterDeleteButton}
                                title="Delete"
                              >
                                <Trash2 size={10} /> 
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="p-12 text-center text-slate-300 font-bold uppercase text-xs">
                          No leave types found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            /* Add / Edit Form Card */
            <form onSubmit={handleSaveOrUpdate} className={masterFormContainer}>
              <div className={masterFormSectionHeader}>
                <h2>{editId ? 'Edit Leave Type Details' : 'Create New Leave Type'}</h2>
              </div>

              <div className={masterFormGrid2}>
                

                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>Leave Name <span className="text-rose-500">*</span></label>
                  <input
                    type="text"
                    name="leaveName"
                    required
                    autoFocus
                    value={formData.leaveName}
                    onChange={(e) => setFormData({...formData, leaveName: e.target.value.toUpperCase()})}
                    className={masterInput}
                    placeholder="Casual, Sick, Maternity, etc"
                  />
                </div>

                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>Alias <span className="text-rose-500">*</span></label>
                  <input
                    type="text"
                    name="leaveCode"
                    required
                    value={formData.leaveCode}
                    onChange={(e) => setFormData({...formData, leaveCode: e.target.value.toUpperCase()})}
                    className={masterInput}
                    placeholder="e.g., CL, SL"
                  />
                </div>

                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>Total Days</label>
                  <input
                    type="number"
                    min="0"
                    name="totalDays"
                    value={formData.totalDays}
                    onChange={handleChange}
                    className={masterInput}
                    placeholder="Enter total days"
                  />
                </div>

                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>Carry Forward</label>
                  <select
                    name="carryForward"
                    value={formData.carryForward}
                    onChange={handleChange}
                    className={masterSelect}
                  >
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>

                <div className={masterFormGridRow}>
                  <label className={masterInputLabel}>Status</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className={masterSelect}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>

                <div className={`${masterFormGridRow} md:col-span-2`}>
                  <label className={masterInputLabel}>Remarks</label>
                  <textarea
                    name="remarks"
                    value={formData.remarks}
                    onChange={handleChange}
                    className={`${masterInput} h-16 resize-none`}
                    placeholder="Enter any remarks..."
                    rows={2}
                  />
                </div>
              </div>

              <div className={masterFormActionBlock}>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-3 py-1 rounded border border-slate-300 text-slate-700 font-semibold text-[11px] bg-white hover:bg-slate-50 transition-all uppercase"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className={masterAccentButton}
                >
                  {isSaving ? (
                    <Loader2 className="animate-spin" size={10} />
                  ) : (
                    <Save size={10} />
                  )}
                  {editId ? 'UPDATE LEAVE TYPE' : 'SAVE LEAVE TYPE'}
                </button>
              </div>
            </form>
          )}
        </main>
      </div>
    </div>
  );
};

export default LeaveTypeMaster;